<template>
  <dialog :class="['modal', { 'modal-open': true }]">
    <div class="modal-box w-[calc(100%-32px)] max-w-[480px] mx-4 sm:mx-auto p-5 sm:p-[30px] rounded-[20px] bg-white border border-[#22ec9b] relative font-['Raleway',sans-serif] shadow-2xl">
      <form method="dialog">
        <button
          class="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 text-[#5b5c60]"
          :disabled="isSubmitting"
          @click.prevent="!isSubmitting && $emit('close')"
        >
          ✕
        </button>
      </form>

      <!-- Header -->
      <div class="flex items-center gap-[10px] mb-[12px]">
        <div class="w-[36px] h-[36px] rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <svg
            class="w-[18px] h-[18px] text-[#dc2626]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </div>
        <div>
          <h3 class="font-bold text-[18px] text-[#043f42] m-0">
            {{ isSuccess ? 'Cohort Deleted' : 'Delete Cohort' }}
          </h3>
          <p class="text-[12px] text-[#5b5c60] m-0">
            This action cannot be undone.
          </p>
        </div>
      </div>

      <div class="w-full h-[1px] bg-[#043f421a] my-[14px]" />

      <!-- Pre-Deletion Confirmation -->
      <div
        v-if="!isSubmitting && !isSuccess"
        class="flex flex-col gap-[8px]"
      >
        <p class="text-[14px] text-[#5b5c60] leading-relaxed m-0">
          Are you sure you want to delete <strong>{{ cohort.name }}</strong>?
        </p>
      </div>

      <!-- Active Progress / Success State -->
      <div
        v-if="isSubmitting || isSuccess"
        class="flex flex-col gap-[14px] py-[10px]"
      >
        <div class="flex justify-between items-center text-[13px] font-semibold text-[#043f42]">
          <span>Status</span>
          <span>{{ isSuccess ? '100%' : `${progress}%` }}</span>
        </div>
        <div class="w-full h-[8px] bg-gray-100 rounded-full overflow-hidden">
          <div
            class="h-full transition-all duration-300 ease-out"
            :class="isSuccess ? 'bg-[#01d680]' : 'bg-[#dc2626]'"
            :style="{ width: `${progress}%` }"
          />
        </div>

        <div class="flex items-center gap-[8px] text-[13px]">
          <span
            v-if="!isSuccess"
            class="loading loading-spinner loading-xs text-[#dc2626]"
          />
          <svg
            v-else
            class="w-[16px] h-[16px] text-[#01d680]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span :class="isSuccess ? 'text-[#00721b] font-bold' : 'text-[#5b5c60] font-medium'">
            {{ statusText }}
          </span>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        class="text-red-600 text-[13px] mt-[12px] p-[10px] bg-red-50 rounded-[10px] border border-red-200"
      >
        {{ error }}
      </div>

      <!-- Actions -->
      <div class="modal-action mt-[20px]">
        <button
          v-if="!isSuccess"
          type="button"
          class="btn btn-ghost rounded-[38px] text-black font-['Raleway',sans-serif] font-bold"
          :disabled="isSubmitting"
          @click.prevent="$emit('close')"
        >
          Cancel
        </button>
        <button
          v-if="!isSuccess"
          type="button"
          class="btn rounded-[38px] bg-[#dc2626] hover:bg-red-700 border-none text-white font-['Raleway',sans-serif] font-bold"
          :disabled="isSubmitting"
          @click.prevent="submitDelete"
        >
          <span
            v-if="isSubmitting"
            class="loading loading-spinner loading-sm"
          />
          {{ isSubmitting ? 'Deleting...' : 'Delete' }}
        </button>
        <button
          v-if="isSuccess"
          type="button"
          class="btn rounded-[38px] bg-[#01d680] hover:bg-[#00c270] border-none text-[#043f42] font-['Raleway',sans-serif] font-bold w-full"
          @click.prevent="finishAndReload"
        >
          Done
        </button>
      </div>
    </div>
    <form
      method="dialog"
      class="modal-backdrop bg-[#00000099]"
    >
      <button
        :disabled="isSubmitting"
        @click.prevent="!isSubmitting && $emit('close')"
      >
        close
      </button>
    </form>
  </dialog>
</template>

<script>
export default {
  name: 'DeleteCohortModal',
  props: {
    cohort: { type: Object, required: true }
  },
  emits: ['close', 'deleted'],
  data () {
    return {
      isSubmitting: false,
      isSuccess: false,
      progress: 0,
      statusText: 'Deleting...',
      error: null
    }
  },
  methods: {
    async submitDelete () {
      this.isSubmitting = true
      this.error = null
      this.progress = 25
      this.statusText = 'Deleting...'

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        // Progressive indicator update
        const timer = setInterval(() => {
          if (this.progress < 85 && !this.isSuccess) {
            this.progress += 20
          }
        }, 150)

        const response = await fetch(`/cohorts/${this.cohort.id}.json`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          }
        })

        clearInterval(timer)
        const data = await response.json().catch(() => ({}))

        if (response.ok && data.success) {
          this.progress = 100
          this.isSuccess = true
          this.statusText = 'Cohort deleted.'
          setTimeout(() => {
            this.finishAndReload()
          }, 900)
        } else {
          this.error = data.error || 'Failed to delete cohort.'
          this.isSubmitting = false
        }
      } catch (err) {
        this.error = 'A network error occurred. Please try again.'
        this.isSubmitting = false
      }
    },
    finishAndReload () {
      this.$emit('deleted', this.cohort.id)
      window.location.reload()
    }
  }
}
</script>
